import { Box, Stack, Typography } from "@mui/material";
import { BankIdentityVerificationChip } from "Components/Shared/Account/BankIdentityVerificationChip";
import { InvalidTokenChip } from "Components/Shared/Account/InvalidTokenChip";
import { PrimaryAccountChip } from "Components/Shared/Account/PrimaryAccountChip";
import { VerificationStatusChip } from "Components/Shared/Account/VerificationStatusChip";
import { BankImage } from "Components/Shared/BankImage";
import { RedactedBalance } from "Components/Shared/RedactedBalance";
import { useIsUserVerified } from "Hooks/useIsUserVerified";
import type { Bank } from "Infrastructure/Api/Api";
import { BankAccountStatus } from "Infrastructure/Api/Api";
import { getLocalBankAccountNumberFromIBAN } from "Utils/AccountUtils";
import { formatCurrency } from "Utils/CurrencyUtils";

type AccountProps = {
  bank: Bank;
  productName: string;
  iban: string;
  balance?: number | null;
  currency: string;
  isPrimary: boolean;
  isPrimaryChipHidden?: boolean;
  status: BankAccountStatus;
  verificationStatusVisibility?: "all" | "none" | "intermediateOnly";
  balanceVariant?: "visible" | "hidden" | "blurred";
  isBankIdentityVerification?: boolean;
  areChipsHidden?: boolean;
  isNameTruncated?: boolean;
  isTokenInvalid?: boolean;
};

export const Account = ({
  bank,
  productName,
  iban,
  balance,
  currency,
  isPrimary,
  isPrimaryChipHidden,
  status,
  balanceVariant = "visible",
  verificationStatusVisibility = "none",
  isBankIdentityVerification = false,
  areChipsHidden = false,
  isNameTruncated = false,
  isTokenInvalid = false,
}: AccountProps) => {
  const { isUserVerified } = useIsUserVerified();
  const accountNumber = getLocalBankAccountNumberFromIBAN(iban);
  const accountBalance = formatCurrency({
    amount: balance,
    currencyCode: currency,
  });

  const isActive = status === BankAccountStatus.Active && !isTokenInvalid;
  const isDisabled =
    status === BankAccountStatus.Inactive ||
    status === BankAccountStatus.Rejected ||
    isTokenInvalid;
  const opacity = isActive ? 1 : isDisabled ? 0.5 : 0.75;
  const isVerificationChipVisible =
    !isTokenInvalid &&
    (verificationStatusVisibility === "all" ||
      (verificationStatusVisibility === "intermediateOnly" &&
        (status === BankAccountStatus.AwaitingVerification ||
          status === BankAccountStatus.Restricted ||
          status === BankAccountStatus.PreApproved)));

  const shouldShowBalance = balanceVariant !== "hidden" && isUserVerified;

  return (
    <Box display="flex" justifyContent="space-between" width="100%">
      <Box display="flex" alignItems="center" gap={3} minWidth={0}>
        <BankImage
          isRounded
          bank={bank}
          size="small"
          sx={{ opacity, flexShrink: 0 }}
          isGrayscale={isDisabled}
        />
        <Stack gap={0.5} minWidth={0}>
          <Typography
            gap={2}
            fontWeight={400}
            display="flex"
            alignItems="center"
            fontSize="0.875rem"
            flexWrap="wrap"
          >
            <Box
              component="span"
              sx={{
                opacity,
                ...(isNameTruncated && {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }),
              }}
            >
              {productName}
            </Box>
            {!areChipsHidden && isPrimary && !isPrimaryChipHidden && (
              <PrimaryAccountChip />
            )}
            {!areChipsHidden && isVerificationChipVisible && (
              <VerificationStatusChip verificationStatus={status} />
            )}
            {!areChipsHidden && isBankIdentityVerification && (
              <BankIdentityVerificationChip />
            )}
            {!areChipsHidden && isTokenInvalid && <InvalidTokenChip />}
          </Typography>
          <Typography fontSize="0.75rem" fontWeight={400} sx={{ opacity }}>
            {accountNumber}
          </Typography>
        </Stack>
      </Box>
      {shouldShowBalance &&
        (isActive ? (
          <Box
            display="flex"
            alignItems="center"
            fontWeight={400}
            sx={
              balanceVariant === "blurred" ? { filter: "blur(3px)" } : undefined
            }
          >
            {accountBalance}
          </Box>
        ) : (
          <RedactedBalance />
        ))}
    </Box>
  );
};
